import type Highcharts from "highcharts";
import type { ManualFailureImpactMetadata } from "../../Chart/PointMetadata";
import { getFailureColor } from "../../useFailureColor";
import type {
  ManualFailureValue,
  IndexedManualFailureValues,
} from "../../../../api/useManualFailureValues";
import type { Project } from "../../../../api/useProjects";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { Quantity } from "../../../../api/useQuantities";
import { MANUAL_FAILURE_IMPACT_SERIE } from "../../Chart/series/serieIds";

function getQuantityFailureImpactsPoints(
  quantities: Quantity[],
  manualFailureValue: ManualFailureValue,
): Highcharts.PointOptionsObject[] {
  return quantities.map(quantity => {
    const value = manualFailureValue.impacts[quantity.id] ?? 0;
    const metadata: ManualFailureImpactMetadata = {
      human: {
        value,
        unit: null,
        precision: null,
        sign: null,
      },
    };
    return {
      name: quantity.name,
      y: value,
      custom: metadata,
    };
  });
}

function getQuantityManualFailureImpactsSeries(
  project: Project,
  quantities: Quantity[],
  analysis: ActiveAnalysis,
  indexedManualFailureValues: IndexedManualFailureValues,
  legendIndex: number,
): Highcharts.SeriesColumnOptions[] {
  const manualFailureValues = indexedManualFailureValues[analysis.id];
  const i = 0;
  const result = [];

  for (const manualFailureId in manualFailureValues) {
    const manualFailureValue = manualFailureValues[manualFailureId];
    if (manualFailureValue === undefined) throw new Error("failure should be defined");
    const data = getQuantityFailureImpactsPoints(quantities, manualFailureValue);
    const color = getFailureColor(project, "MANUAL_FAILURE", manualFailureValue.manualFailureName);

    result.push({
      name: manualFailureValue.manualFailureName,
      custom: { failureDisplayName: manualFailureValue.manualFailureName },
      id: `${MANUAL_FAILURE_IMPACT_SERIE}-${manualFailureValue.manualFailureName}`,
      data,
      color,
      borderColor: color,
      stack: "1",
      type: "column" as const,
      replicateInNavigator: false,
      index: i,
      showInLegend: true,
    });
  }

  return result
    .sort(comparatorBy("name"))
    .map((s, i) => ({ ...s, legendIndex: legendIndex + 2 + i }));
}

export default getQuantityManualFailureImpactsSeries;
